import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/toast/use-toast';
import { 
  PencilIcon, 
  BarChart2, 
  Rocket,
  LogIn,
  TrendingUp,
  Eye,
  Users,
  Clock,
  AlertCircle,
  Map,
  Filter,
  ArrowUp,
  ArrowDown,
  Plus,
  Search,
  LayoutGrid,
  List as ListIcon,
  Building,
  Home,
  Calendar,
  Target,
  MousePointer,
  MessageCircle,
  Globe,
  Shield,
  Activity,
  RefreshCw,
  Settings,
  Star,
  MapPin,
  DollarSign
} from 'lucide-react';

const CLOUDFLARE_PROPERTIES_URL = 'https://pub-e51257e7234a40ea96269a0ac4d1c6ab.r2.dev';

const getPropertyImageUrl = (imageNames) => {
  if (!imageNames || imageNames.length === 0) return null;
  
  const imageName = imageNames[0];
  
  if (imageName.startsWith('http://') || imageName.startsWith('https://')) {
    return imageName;
  }
  
  return `${CLOUDFLARE_PROPERTIES_URL}/${imageName}`;
};

// Componente para el header mejorado
const PageHeader = ({ icon: Icon, title, subtitle, actionButton, stats }) => (
  <div className="mb-8">
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
      <div className="flex items-center gap-4 mb-4 lg:mb-0">
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-3 rounded-xl shadow-lg">
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{title}</h1>
          {subtitle && <p className="text-gray-600 dark:text-gray-400 mt-1">{subtitle}</p>}
        </div>
      </div>
      {actionButton}
    </div>
    
    {/* Quick overview bar */}
    {stats && (
      <div className="backdrop-blur-xl bg-white/80 dark:bg-white/5 rounded-xl shadow-sm border border-gray-100 dark:border-white/10 p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.totalListings || 0}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Publicaciones</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.activeListings || 0}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Activas</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats.totalViews || 0}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Visitas Totales</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats.averageTime || '0s'}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Tiempo Promedio</p>
          </div>
        </div>
      </div>
    )}
  </div>
);

// Componente de estadísticas mejorado
const EnhancedStatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue', onClick, isClickable = false }) => {
  const colorClasses = {
    blue: { bg: 'bg-blue-50 dark:bg-blue-500/20', text: 'text-blue-600 dark:text-blue-400', hover: 'hover:bg-blue-100 dark:hover:bg-blue-500/30' },
    green: { bg: 'bg-green-50 dark:bg-green-500/20', text: 'text-green-600 dark:text-green-400', hover: 'hover:bg-green-100 dark:hover:bg-green-500/30' },
    purple: { bg: 'bg-purple-50 dark:bg-purple-500/20', text: 'text-purple-600 dark:text-purple-400', hover: 'hover:bg-purple-100 dark:hover:bg-purple-500/30' },
    amber: { bg: 'bg-amber-50 dark:bg-amber-500/20', text: 'text-amber-600 dark:text-amber-400', hover: 'hover:bg-amber-100 dark:hover:bg-amber-500/30' },
    indigo: { bg: 'bg-indigo-50 dark:bg-indigo-500/20', text: 'text-indigo-600 dark:text-indigo-400', hover: 'hover:bg-indigo-100 dark:hover:bg-indigo-500/30' },
    pink: { bg: 'bg-pink-50 dark:bg-pink-500/20', text: 'text-pink-600 dark:text-pink-400', hover: 'hover:bg-pink-100 dark:hover:bg-pink-500/30' }
  };
  
  const colors = colorClasses[color] || colorClasses.blue;
  
  return (
    <div 
      className={`backdrop-blur-xl bg-white/80 dark:bg-white/5 rounded-xl shadow-sm border border-gray-100 dark:border-white/10 p-6 transition-all duration-300 hover:shadow-md dark:hover:shadow-xl ${
        isClickable ? `cursor-pointer ${colors.hover}` : ''
      }`}
      onClick={isClickable ? onClick : undefined}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 rounded-xl ${colors.bg}`}>
          <Icon className={`w-6 h-6 ${colors.text}`} />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-sm font-medium ${
            trend >= 0 ? 'bg-green-50 dark:bg-green-500/20 text-green-700 dark:text-green-400' : 'bg-red-50 dark:bg-red-500/20 text-red-700 dark:text-red-400'
          }`}>
            {trend >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{title}</h3>
      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">{value}</p>
      {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>}
    </div>
  );
};

// Componente de botón de acción mejorado
const ActionButton = ({ icon: Icon, label, onClick, variant = 'primary', size = 'default' }) => {
  const variants = {
    primary: 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg',
    secondary: 'backdrop-blur-xl bg-white/80 dark:bg-white/10 hover:bg-gray-50 dark:hover:bg-white/20 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-white/20 shadow-sm',
    success: 'bg-green-500 hover:bg-green-600 text-white shadow-lg',
    warning: 'bg-amber-500 hover:bg-amber-600 text-white shadow-lg',
    danger: 'bg-red-50 dark:bg-red-500/20 hover:bg-red-100 dark:hover:bg-red-500/30 text-red-600 dark:text-red-400 border border-red-100 dark:border-red-500/20'
  };
  
  const sizes = {
    sm: 'px-3 py-2 text-sm',
    default: 'px-4 py-2.5',
    lg: 'px-6 py-3 text-lg'
  };
  
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 ${variants[variant]} ${sizes[size]}`}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {label}
    </button>
  );
};

// Componente de estado vacío mejorado
const EmptyState = ({ icon: Icon, title, description, action, variant = 'default' }) => (
  <div className="min-h-[400px] flex items-center justify-center">
    <div className="max-w-md w-full backdrop-blur-xl bg-white/80 dark:bg-white/5 rounded-2xl shadow-lg border border-gray-100 dark:border-white/10 p-8 text-center">
      <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6 ${
        variant === 'error' ? 'bg-red-50 dark:bg-red-500/20' : 'bg-blue-50 dark:bg-blue-500/20'
      }`}>
        <Icon className={`w-8 h-8 ${variant === 'error' ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'}`} />
      </div>
      <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">{title}</h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">{description}</p>
      {action}
    </div>
  </div>
);

// Componente de carga mejorado
const LoadingSpinner = () => (
  <div className="min-h-[400px] flex items-center justify-center">
    <div className="text-center">
      <div className="relative mx-auto w-16 h-16 mb-4">
        <div className="absolute inset-0 rounded-full border-4 border-gray-200 dark:border-gray-700"></div>
        <div className="absolute inset-0 rounded-full border-4 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
      </div>
      <p className="text-gray-600 dark:text-gray-400 font-medium">Cargando publicaciones...</p>
      <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">Obteniendo estadísticas actualizadas</p>
    </div>
  </div>
);

// Componente de tarjeta de listado mejorado
const ListingCard = ({ listing, onEdit, onStatistics, onBoost }) => {
  const formatPrice = (listing) => {
    if (listing.listingType === 'rent') {
      return `S/ ${new Intl.NumberFormat('es-PE').format(listing.precio_mensual || 0)}/mes`;
    }
    return `S/ ${new Intl.NumberFormat('es-PE').format(listing.precio_soles || 0)}`;
  };

  const getStatusColor = (estado) => {
    switch (estado?.toLowerCase()) {
      case 'activo': return 'bg-green-50 dark:bg-green-500/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-500/30';
      case 'pausado': return 'bg-yellow-50 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-500/30';
      case 'vendido': return 'bg-blue-50 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/30';
      default: return 'bg-gray-50 dark:bg-gray-500/20 text-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-500/30';
    }
  };

  const getTypeColor = (type) => {
    return type === 'sale' 
      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' 
      : 'bg-gradient-to-r from-purple-500 to-purple-600 text-white';
  };

  return (
    <div className="backdrop-blur-xl bg-white/80 dark:bg-white/5 rounded-2xl shadow-sm border border-gray-100 dark:border-white/10 overflow-hidden transition-all duration-300 hover:shadow-lg dark:hover:shadow-xl hover:-translate-y-1">
      {/* Image Section */}
      <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 relative overflow-hidden">
        {listing.imagenes && listing.imagenes[0] ? (
          <img 
            src={getPropertyImageUrl(listing.imagenes)} 
            alt={listing.titulo || 'Propiedad'} 
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
            onError={(e) => {
              e.target.src = '/default-property-image.jpg';
              e.target.className = 'w-full h-full object-contain p-4 bg-gray-100 dark:bg-gray-800';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center">
              <Building className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Sin imagen</p>
            </div>
          </div>
        )}
        
        {/* Overlays */}
        <div className="absolute top-3 left-3">
          <div className={`px-3 py-1 text-xs font-semibold rounded-lg shadow-lg ${getTypeColor(listing.listingType)}`}>
            {listing.typeLabel}
          </div>
        </div>
        
        <div className="absolute top-3 right-3">
          <div className={`px-3 py-1 text-xs font-medium rounded-lg border ${getStatusColor(listing.estado)}`}>
            {listing.estado}
          </div>
        </div>

        {listing.destacada && (
          <div className="absolute bottom-3 left-3">
            <div className="bg-gradient-to-r from-amber-400 to-orange-500 text-white px-2 py-1 rounded-lg flex items-center gap-1 text-xs font-medium shadow-lg">
              <Star className="w-3 h-3" />
              Destacada
            </div>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-5">
        <div className="mb-3">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 line-clamp-2 text-lg">{listing.titulo}</h3>
          <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400 text-sm mb-2">
            <MapPin className="w-4 h-4" />
            <span className="line-clamp-1">{listing.ubicacion}</span>
          </div>
        </div>

        <div className="mb-4">
          <div className="flex items-center gap-1 mb-1">
            <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
            <span className="font-bold text-xl text-gray-900 dark:text-gray-100">{formatPrice(listing)}</span>
          </div>
          {listing.precio_dolares && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              USD {new Intl.NumberFormat('en-US').format(listing.precio_dolares)}
            </p>
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4 py-3 bg-gray-50 dark:bg-gray-500/20 rounded-lg">
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{listing.habitaciones || '0'}</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Habitaciones</p>
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{listing.banos || '0'}</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Baños</p>
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{listing.area_total || '0'}m²</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Área</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button 
            onClick={() => onEdit(listing)}
            className="flex-1 bg-blue-50 dark:bg-blue-500/20 hover:bg-blue-100 dark:hover:bg-blue-500/30 text-blue-700 dark:text-blue-400 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-1"
          >
            <PencilIcon className="w-4 h-4" />
            Editar
          </button>
          <button
            onClick={() => onStatistics(listing)}
            className="flex-1 bg-purple-50 dark:bg-purple-500/20 hover:bg-purple-100 dark:hover:bg-purple-500/30 text-purple-700 dark:text-purple-400 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-1"
          >
            <BarChart2 className="w-4 h-4" />
            Stats
          </button>
          <button
            onClick={() => onBoost(listing)}
            className="flex-1 bg-amber-50 dark:bg-amber-500/20 hover:bg-amber-100 dark:hover:bg-amber-500/30 text-amber-700 dark:text-amber-400 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-1"
          >
            <Rocket className="w-4 h-4" />
            Impulsar
          </button>
        </div>
      </div>
    </div>
  );
};

// Componente principal
const MyListings = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userType, setUserType] = useState(null);
  const [stats, setStats] = useState(null);
  const [userTypeAnalysis, setUserTypeAnalysis] = useState(null);
  const { toast } = useToast();
  
  // Estados para filtrado y ordenación
  const [viewMode, setViewMode] = useState('grid'); // 'list' o 'grid'
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'sale', 'rent'
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'activo', 'pausado'
  const [sortBy, setSortBy] = useState('date'); // 'date', 'price', 'views'
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc', 'desc'

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No hay token disponible');
    
        const headers = { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };
    
        const fetchWithErrorHandling = async (url, errorMessage) => {
          const response = await fetch(url, { headers });
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('Error detallado:', errorData);
            throw new Error(`${errorMessage}: ${response.status} ${response.statusText}`);
          }
          return response.json();
        };
    
        const [listingsData, alquileresData, statsData, userData, averageVisitTimeData] = await Promise.all([
          fetchWithErrorHandling(
            `${process.env.REACT_APP_API_URL || 'https://backend-production-1786.up.railway.app'}/api/vendedores/${user.id}/propiedades/public`,
            'Error al cargar las propiedades'
          ),
          fetchWithErrorHandling(
            `${process.env.REACT_APP_API_URL || 'https://backend-production-1786.up.railway.app'}/api/vendedores/${user.id}/alquileres/public`,
            'Error al cargar los alquileres'
          ),
          fetchWithErrorHandling(
            `${process.env.REACT_APP_API_URL || 'https://backend-production-1786.up.railway.app'}/api/statistics`,
            'Error al cargar las estadísticas'
          ),
          fetchWithErrorHandling(
            `${process.env.REACT_APP_API_URL || 'https://backend-production-1786.up.railway.app'}/api/vendedores/perfil`,
            'Error al cargar el perfil del usuario'
          ),
          fetchWithErrorHandling(
            `${process.env.REACT_APP_API_URL || 'https://backend-production-1786.up.railway.app'}/api/statistics/average-visit-time`,
            'Error al cargar el tiempo promedio de visita'
          )
        ]);
        
        // Calcular ciudades únicas
        const uniqueCities = new Set(
          statsData?.geographicDistribution?.map(city => city.ubicacion)
        );

        // Ensure arrays
        const propertyList = Array.isArray(listingsData.propiedades) 
          ? listingsData.propiedades 
          : (listingsData.propiedades || []);
        
        const rentalsList = Array.isArray(alquileresData.alquileres) 
          ? alquileresData.alquileres 
          : (alquileresData.alquileres || []);
        
        // Mark properties as sale
        const propiedades = propertyList.map(property => ({
          ...property,
          listingType: 'sale',
          typeLabel: 'Venta',
          uniqueId: `property-${property.id}`
        }));
        
        // Mark rentals as rent
        const alquileres = rentalsList.map(rental => ({
          ...rental,
          listingType: 'rent',
          typeLabel: 'Alquiler',
          uniqueId: `rental-${rental.id}`
        }));
        
        // Combine both lists
        const combinedListings = [...propiedades, ...alquileres];
        setListings(combinedListings);
        
        // Enhanced stats
        const enhancedStats = {
          ...statsData,
          summary: {
            ...statsData.summary,
            uniqueLocations: uniqueCities.size || 0
          },
          averageVisitTime: averageVisitTimeData.formattedTime || '0s',
          // Quick overview stats
          totalListings: combinedListings.length,
          activeListings: combinedListings.filter(l => l.estado === 'Activo').length,
          totalViews: statsData?.summary?.totalVisits || 0,
          averageTime: averageVisitTimeData.formattedTime || '0s'
        };
        
        setStats(enhancedStats);
        setUserType(userData.tipo_usuario_id);

        // Show success toast
        toast({
          title: "Datos actualizados",
          description: `Se cargaron ${combinedListings.length} publicaciones con estadísticas actualizadas.`,
          variant: "success"
        });
        
      } catch (error) {
        console.error('Error:', error);
        setError(error.message);
        toast({
          title: "Error al cargar datos",
          description: error.message,
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };
  
    if (isAuthenticated) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user, isAuthenticated, toast]);

  // Manejar acciones
  const handleEdit = (listing) => {
    const editRoute = listing.listingType === 'rent' 
      ? `/editar-alquiler/${listing.id}` 
      : `/editar-propiedad/${listing.id}`;
    
    navigate(editRoute);
  };
  
  const handleStatistics = (listing) => {
    const listingType = listing.listingType === 'rent' ? 'rental' : 'property';
    navigate(`/statistics/${listing.id}?type=${listingType}`, {
      state: { 
        listingType: listing.listingType,
        listingTitle: listing.titulo
      }
    });
  };
  
  const handleBoost = (listing) => {
    const listingType = listing.listingType === 'rent' ? 'rental' : 'property';
    navigate(`/flyers/${listing.id}?type=${listingType}`, {
      state: { 
        listingType: listing.listingType,
        listingTitle: listing.titulo
      }
    });
  };
  
  const handleAddNew = () => {
    navigate('/publicar');
  };

  const handleRefresh = async () => {
    setLoading(true);
    // Trigger a refresh by re-running the effect
    window.location.reload();
  };

  // Filtrar y ordenar listados
  const getFilteredListings = () => {
    let filtered = [...listings]; 
    
    // Filtrar por tipo
    if (filterType !== 'all') {
      filtered = filtered.filter(listing => listing.listingType === filterType);
    }
    
    // Filtrar por estado
    if (filterStatus !== 'all') {
      filtered = filtered.filter(listing => listing.estado?.toLowerCase() === filterStatus);
    }
    
    // Filtrar por búsqueda
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(listing => 
        listing.titulo?.toLowerCase().includes(query) || 
        listing.ubicacion?.toLowerCase().includes(query)
      );
    }
    
    // Ordenar
    return filtered.sort((a, b) => {
      if (sortBy === 'date') {
        const dateA = new Date(a.fecha_publicacion || 0);
        const dateB = new Date(b.fecha_publicacion || 0);
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      }
      
      if (sortBy === 'price') {
        const priceA = a.listingType === 'rent' ? (a.precio_mensual || 0) : (a.precio_soles || 0);
        const priceB = b.listingType === 'rent' ? (b.precio_mensual || 0) : (b.precio_soles || 0);
        return sortDirection === 'asc' ? priceA - priceB : priceB - priceA;
      }
      
      if (sortBy === 'views') {
        const viewsA = a.visitas || 0;
        const viewsB = b.visitas || 0;
        return sortDirection === 'asc' ? viewsA - viewsB : viewsB - viewsA;
      }
      
      return 0;
    });
  };
  
  const getListingKey = (listing) => {
    return listing.uniqueId || `${listing.listingType}-${listing.id}`;
  };

  // Estados condicionales
  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <EmptyState
        icon={AlertCircle}
        title="Ha ocurrido un error"
        description={error}
        variant="error"
        action={
          <div className="space-y-3">
            <ActionButton
              icon={RefreshCw}
              label="Reintentar"
              onClick={handleRefresh}
              variant="primary"
            />
            <ActionButton
              icon={Settings}
              label="Configuración"
              onClick={() => navigate('/settings')}
              variant="secondary"
              size="sm"
            />
          </div>
        }
      />
    );
  }

  if (!isAuthenticated) {
    return (
      <EmptyState
        icon={LogIn}
        title="Inicia sesión para continuar"
        description="Necesitas iniciar sesión para acceder a tus publicaciones y ver las estadísticas de rendimiento."
        action={
          <ActionButton
            icon={LogIn}
            label="Iniciar Sesión"
            onClick={() => navigate('/login')}
            variant="primary"
          />
        }
      />
    );
  }

  if (userType === 1) {
    return (
      <EmptyState
        icon={Users}
        title="Conviértete en vendedor"
        description="Actualiza tu perfil a vendedor para publicar propiedades y acceder a las estadísticas avanzadas."
        action={
          <ActionButton
            icon={TrendingUp}
            label="Actualizar a Vendedor"
            onClick={() => navigate('/upgrade-user')}
            variant="success"
          />
        }
      />
    );
  }

  const filteredListings = getFilteredListings();

  return (
    <div className="bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header mejorado */}
        <PageHeader
          icon={BarChart2}
          title="Mis Publicaciones"
          subtitle="Gestiona tus propiedades y analiza su rendimiento con estadísticas en tiempo real"
          stats={stats}
          actionButton={
            <div className="flex gap-3">
              <ActionButton
                icon={RefreshCw}
                label="Actualizar"
                onClick={handleRefresh}
                variant="secondary"
              />
              <ActionButton
                icon={Plus}
                label="Nueva Publicación"
                onClick={handleAddNew}
                variant="primary"
              />
            </div>
          }
        />

        {/* Stats Overview Mejorado */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <EnhancedStatCard
              title="Visitas Totales"
              value={new Intl.NumberFormat('es-PE').format(stats?.summary?.totalVisits || 0)}
              subtitle="Todos los usuarios"
              icon={Eye}
              trend={stats?.trends?.visitsChange}
              color="blue"
            />
            <EnhancedStatCard
              title="Publicaciones Activas"
              value={listings.filter(l => l.estado === 'Activo').length}
              subtitle={`de ${listings.length} totales`}
              icon={TrendingUp}
              color="green"
            />
            <EnhancedStatCard
              title="Tiempo Promedio"
              value={stats?.averageVisitTime || "0s"}
              subtitle="Por visita"
              icon={Clock}
              color="purple"
            />
            <EnhancedStatCard
              title="Clicks de Contacto"
              value={new Intl.NumberFormat('es-PE').format(stats?.summary?.contactClicks || 0)}
              subtitle="Conversiones"
              icon={MousePointer}
              color="amber"
            />
            <EnhancedStatCard
              title="Ciudades Alcanzadas"
              value={stats.summary?.uniqueLocations || 0}
              subtitle="Distribución geográfica"
              icon={Map}
              color="indigo"
            />
          </div>
        )}

        {/* Filtros y Controles Mejorados */}
        <div className="backdrop-blur-xl bg-white/80 dark:bg-white/5 rounded-xl shadow-sm border border-gray-100 dark:border-white/10 p-6 mb-8">
          <div className="flex flex-col space-y-4">
            {/* Primera fila: Búsqueda y filtros principales */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Search */}
              <div className="relative md:w-80">
                <Search className="absolute left-3 top-3 text-gray-400 dark:text-gray-500 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Buscar por título o ubicación..."
                  className="pl-10 pr-4 py-2.5 w-full border border-gray-200 dark:border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/50 dark:bg-white/10 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              {/* View Toggle */}
              <div className="inline-flex rounded-lg border border-gray-200 dark:border-white/20 overflow-hidden bg-gray-50 dark:bg-gray-500/20">
                <button
                  className={`p-2.5 transition-colors ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/20'}`}
                  onClick={() => setViewMode('grid')}
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  className={`p-2.5 transition-colors ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/20'}`}
                  onClick={() => setViewMode('list')}
                >
                  <ListIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {/* Segunda fila: Filtros avanzados */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Filter by Type */}
              <div className="inline-flex rounded-lg border border-gray-200 dark:border-white/20 overflow-hidden bg-gray-50 dark:bg-gray-500/20">
                <button
                  className={`px-4 py-2 text-sm font-medium transition-colors ${filterType === 'all' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/20'}`}
                  onClick={() => setFilterType('all')}
                >
                  Todos ({listings.length})
                </button>
                <button
                  className={`px-4 py-2 text-sm font-medium transition-colors ${filterType === 'sale' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/20'}`}
                  onClick={() => setFilterType('sale')}
                >
                  Venta ({listings.filter(l => l.listingType === 'sale').length})
                </button>
                <button
                  className={`px-4 py-2 text-sm font-medium transition-colors ${filterType === 'rent' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/20'}`}
                  onClick={() => setFilterType('rent')}
                >
                  Alquiler ({listings.filter(l => l.listingType === 'rent').length})
                </button>
              </div>
              
              {/* Filter by Status */}
              <div className="inline-flex rounded-lg border border-gray-200 dark:border-white/20 overflow-hidden bg-gray-50 dark:bg-gray-500/20">
                <button
                  className={`px-4 py-2 text-sm font-medium transition-colors ${filterStatus === 'all' ? 'bg-green-500 text-white' : 'bg-white dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/20'}`}
                  onClick={() => setFilterStatus('all')}
                >
                  Todos
                </button>
                <button
                  className={`px-4 py-2 text-sm font-medium transition-colors ${filterStatus === 'activo' ? 'bg-green-500 text-white' : 'bg-white dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/20'}`}
                  onClick={() => setFilterStatus('activo')}
                >
                  Activos
                </button>
                <button
                  className={`px-4 py-2 text-sm font-medium transition-colors ${filterStatus === 'pausado' ? 'bg-green-500 text-white' : 'bg-white dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/20'}`}
                  onClick={() => setFilterStatus('pausado')}
                >
                  Pausados
                </button>
              </div>
              
              {/* Sort By */}
              <select
                className="px-4 py-2 border border-gray-200 dark:border-white/20 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500
                         [&>option]:bg-white [&>option]:dark:bg-gray-800 [&>option]:text-gray-900 [&>option]:dark:text-gray-100 [&>option]:py-2 [&>option]:px-3"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="date">Ordenar por fecha</option>
                <option value="price">Ordenar por precio</option>
                <option value="views">Ordenar por visitas</option>
              </select>
              
              {/* Sort Direction */}
              <button
                className="p-2.5 border border-gray-200 dark:border-white/20 rounded-lg bg-white dark:bg-white/10 hover:bg-gray-50 dark:hover:bg-white/20 transition-colors"
                onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                title={sortDirection === 'asc' ? 'Cambiar a descendente' : 'Cambiar a ascendente'}
              >
                {sortDirection === 'asc' ? <ArrowUp className="w-4 h-4 text-gray-600 dark:text-gray-400" /> : <ArrowDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />}
              </button>
              
              {/* Results count */}
              <div className="ml-auto text-sm text-gray-600 dark:text-gray-400">
                {filteredListings.length} de {listings.length} publicaciones
              </div>
            </div>
          </div>
        </div>

        {/* Listings Content */}
        {filteredListings.length === 0 ? (
          <EmptyState
            icon={searchQuery ? Search : Building}
            title={searchQuery ? "No hay resultados" : "No hay publicaciones"}
            description={
              searchQuery 
                ? `No se encontraron publicaciones que coincidan con "${searchQuery}".`
                : "Aún no has publicado ninguna propiedad. ¡Comienza creando tu primera publicación!"
            }
            action={
              <div className="space-y-3">
                {searchQuery && (
                  <ActionButton
                    icon={Search}
                    label="Limpiar búsqueda"
                    onClick={() => setSearchQuery('')}
                    variant="secondary"
                    size="sm"
                  />
                )}
                <ActionButton
                  icon={Plus}
                  label="Crear Primera Publicación"
                  onClick={handleAddNew}
                  variant="primary"
                />
              </div>
            }
          />
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredListings.map((listing) => (
              <ListingCard
                key={getListingKey(listing)}
                listing={listing}
                onEdit={handleEdit}
                onStatistics={handleStatistics}
                onBoost={handleBoost}
              />
            ))}
          </div>
        ) : (
          <div className="backdrop-blur-xl bg-white/80 dark:bg-white/5 rounded-xl shadow-sm border border-gray-100 dark:border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-white/10 bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-800/50 dark:to-blue-900/50">
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Propiedad</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Ubicación</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Precio</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Tipo</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Fecha</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                  {filteredListings.map((listing) => (
                    <tr key={getListingKey(listing)} className="hover:bg-blue-50 dark:hover:bg-blue-500/20 transition-colors duration-200">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                            {listing.imagenes && listing.imagenes[0] ? (
                              <img 
                                src={getPropertyImageUrl(listing.imagenes)} 
                                alt={listing.titulo} 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Building className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-gray-100 line-clamp-1">{listing.titulo}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {listing.habitaciones}h • {listing.banos}b • {listing.area_total}m²
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                          <span className="text-sm text-gray-900 dark:text-gray-100 line-clamp-1">{listing.ubicacion}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {listing.listingType === 'rent' 
                            ? `S/ ${new Intl.NumberFormat('es-PE').format(listing.precio_mensual || 0)}/mes` 
                            : `S/ ${new Intl.NumberFormat('es-PE').format(listing.precio_soles || 0)}`
                          }
                        </div>
                        {listing.precio_dolares && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            USD {new Intl.NumberFormat('en-US').format(listing.precio_dolares)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          listing.listingType === 'sale' 
                            ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-400' 
                            : 'bg-purple-100 dark:bg-purple-500/20 text-purple-800 dark:text-purple-400'
                        }`}>
                          {listing.listingType === 'sale' ? (
                            <Home className="w-3 h-3 mr-1" />
                          ) : (
                            <Building className="w-3 h-3 mr-1" />
                          )}
                          {listing.typeLabel}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          listing.estado === 'Activo' 
                            ? 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-400' 
                            : listing.estado === 'Pausado'
                            ? 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-400'
                            : 'bg-gray-100 dark:bg-gray-500/20 text-gray-800 dark:text-gray-400'
                        }`}>
                          <div className={`w-2 h-2 rounded-full mr-2 ${
                            listing.estado === 'Activo' 
                              ? 'bg-green-500 dark:bg-green-400' 
                              : listing.estado === 'Pausado'
                              ? 'bg-yellow-500 dark:bg-yellow-400'
                              : 'bg-gray-500 dark:bg-gray-400'
                          }`}></div>
                          {listing.estado}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {listing.fecha_publicacion 
                          ? new Date(listing.fecha_publicacion).toLocaleDateString('es-PE', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })
                          : '-'
                        }
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button 
                            onClick={() => handleEdit(listing)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium hover:underline transition-colors"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleStatistics(listing)}
                            className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 text-sm font-medium hover:underline transition-colors"
                          >
                            Stats
                          </button>
                          <button
                            onClick={() => handleBoost(listing)}
                            className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 text-sm font-medium hover:underline transition-colors"
                          >
                            Impulsar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Footer con información adicional */}
        {filteredListings.length > 0 && (
          <div className="mt-8 backdrop-blur-xl bg-gradient-to-r from-blue-50/80 to-purple-50/80 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-6 border border-blue-100 dark:border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-blue-500 p-2 rounded-lg">
                  <Activity className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">Estadísticas en Tiempo Real</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Datos actualizados automáticamente • Incluye usuarios anónimos y registrados
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-700 dark:text-gray-300">Usuarios Registrados</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                  <span className="text-gray-700 dark:text-gray-300">Usuarios Anónimos</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyListings;